import type { BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import type { PublicBookingConfig } from "@/types";

function normalizeSoldOutNightIso(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 10 && trimmed[4] === "-") {
    return trimmed.slice(0, 10);
  }
  const german = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (german) {
    return `${german[3]}-${german[2]}-${german[1]}`;
  }
  return trimmed;
}

export function soldOutNightsSet(config: PublicBookingConfig | null | undefined): Set<string> {
  const nights = config?.tourist_sold_out_nights ?? [];
  return new Set(
    nights
      .map((night) => normalizeSoldOutNightIso(String(night)))
      .filter((night) => /^\d{4}-\d{2}-\d{2}$/.test(night)),
  );
}

/** Übernachtungsnächte: Anreisetag inklusive, Abreisetag exklusive. */
export function bookingNightDates(arrival: string, departure: string): string[] {
  const nights: string[] = [];
  let current = arrival;
  while (current < departure) {
    nights.push(current);
    const [y, m, d] = current.split("-").map(Number);
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
    dt.setDate(dt.getDate() + 1);
    const ny = dt.getFullYear();
    const nm = String(dt.getMonth() + 1).padStart(2, "0");
    const nd = String(dt.getDate()).padStart(2, "0");
    current = `${ny}-${nm}-${nd}`;
  }
  return nights;
}

export function soldOutNightsInStay(
  arrival: string,
  departure: string,
  soldOut: Set<string>,
): string[] {
  if (!arrival || !departure || departure <= arrival || soldOut.size === 0) return [];
  return bookingNightDates(arrival, departure).filter((night) => soldOut.has(night));
}

export function validateSoldOutNights(
  arrival: string,
  departure: string,
  config: PublicBookingConfig | null | undefined,
  messages: Pick<BookingMessages["validation"], "soldOutArrival"> = de.validation,
): string | null {
  const soldOut = soldOutNightsSet(config);
  if (soldOut.size === 0) return null;
  const blocked = soldOutNightsInStay(arrival, departure, soldOut);
  if (blocked.length === 0) return null;
  const message = config?.tourist_sold_out_message?.trim() || messages.soldOutArrival;
  const first = blocked[0]!.split("-").reverse().join(".");
  if (blocked.length === 1) {
    return `${message} (betroffene Nacht: ${first})`;
  }
  const last = blocked[blocked.length - 1]!.split("-").reverse().join(".");
  return `${message} (betroffene Nächte: ${first} – ${last})`;
}

export function isArrivalBlockedBySoldOut(iso: string, soldOut: Set<string>): boolean {
  return soldOut.has(iso);
}

export function isDepartureBlockedBySoldOut(
  iso: string,
  arrival: string,
  soldOut: Set<string>,
): boolean {
  if (!arrival || iso <= arrival) return false;
  return soldOutNightsInStay(arrival, iso, soldOut).length > 0;
}

function firstTrimmedMessage(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/** Block-Hinweis aus Backend-/Kategorien-Antworten (unabhängig von der Client-Config). */
export function resolveBackendPeriodBlockMessage(options?: {
  backendPeriodBlock?: string | null;
  periodCategoriesBlock?: string | null;
  arrivalTypeCategoriesBlock?: string | null;
  categoriesBlock?: string | null;
}): string | null {
  return firstTrimmedMessage(
    options?.backendPeriodBlock,
    options?.periodCategoriesBlock,
    options?.arrivalTypeCategoriesBlock,
    options?.categoriesBlock,
  );
}

/** Zeitraum durch ausgebuchte Nächte (Tourist-Anbindung) blockiert. */
export function hasSoldOutPeriodBlock(
  arrival: string,
  departure: string,
  config: PublicBookingConfig | null | undefined,
  backendPeriodBlock?: string | null,
  extraBackendBlock?: string | null,
): boolean {
  if (firstTrimmedMessage(backendPeriodBlock, extraBackendBlock)) return true;
  return soldOutNightsInStay(arrival, departure, soldOutNightsSet(config)).length > 0;
}
